// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Building, User, RefreshCw, Briefcase, MapPin, Globe, Linkedin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { setStoredToken } from '../../utils/authFetch';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [form, setForm] = useState({
    email: '', password: '', confirm_password: '', full_name: '',
    company_name: '', job_title: '', industry: '', company_location: '',
    company_website: '', company_linkedin: '', company_description: '',
  });
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const submitting = useRef(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const apiFetch = async (url, body) => {
    const res = await fetch(`${BACKEND}${url}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    let data = {};
    try {
      const text = await res.text();
      data = text ? JSON.parse(text) : {};
    } catch (_) { /* body already consumed */ }
    if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
    return data;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    submitting.current = true;
    setLoading(true);
    try {
      const { confirm_password, ...payload } = form;
      const data = await apiFetch('/api/auth/register', payload);
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStep('verify');
    } catch (err) {
      if (err.message.includes('409') || err.message.toLowerCase().includes('already exists')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(err.message);
      }
    }
    finally { setLoading(false); submitting.current = false; }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch('/api/auth/verify-email', { email: form.email, code: otp, purpose: 'email_verify' });
      // Account is created + verified → user is logged in. Send them to choose a plan
      // (Gallery + core features stay locked until an active subscription is purchased).
      if (data.session_token) setStoredToken(data.session_token);
      if (data.user) setUser(data.user);
      window.location.replace('/welcome');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true); setError(''); setMessage('');
    try {
      const data = await apiFetch('/api/auth/resend-verification', { email: form.email });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setMessage('New code sent.');
    } catch { setMessage('New code sent.'); }
    finally { setResending(false); }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const passwordMatch = form.confirm_password && form.password === form.confirm_password;
  const passwordMismatch = form.confirm_password && form.password !== form.confirm_password;

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0a0a0a] flex-col justify-between p-12">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">FashionSpyder</h1>
          <p className="text-white/50 text-sm mt-1">HR Intelligence Platform</p>
        </div>
        <div className="space-y-4">
          {['Verified fashion professionals', 'AI-powered matching & scoring', '20+ advanced filters', 'Real-time talent notifications'].map(item => (
            <div key={item} className="flex items-center gap-3">
              <div className="w-1 h-1 bg-white/60 rounded-full flex-shrink-0" />
              <p className="text-white/70 text-sm">{item}</p>
            </div>
          ))}
        </div>
        <p className="text-white/20 text-xs">&copy; 2025 FashionSpyder</p>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-sm">

          {/* ── Registration form ── */}
          {step === 'form' && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
                <p className="text-muted-foreground text-sm mt-1">Company email addresses only</p>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

              {/* Google OAuth */}
              <button onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 border border-border py-3 text-sm hover:bg-secondary transition-colors mb-1">
                <GoogleIcon /> Sign up with Google Workspace
              </button>
              <p className="text-xs text-muted-foreground text-center mb-5">Google Workspace company emails only — not personal Gmail</p>

              <div className="relative mb-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs text-muted-foreground bg-white px-2">or register with company email</div>
              </div>

              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Full Name</label>
                  <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={form.full_name} onChange={f('full_name')} required placeholder="Jane Smith" className="pl-10 rounded-none" /></div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Work Email</label>
                  <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="email" value={form.email} onChange={f('email')} required placeholder="you@company.com" className="pl-10 rounded-none" /></div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Company</label>
                  <div className="relative"><Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={form.company_name} onChange={f('company_name')} required placeholder="Acme Fashion Group" className="pl-10 rounded-none" /></div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Job Title</label>
                  <div className="relative"><Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={form.job_title} onChange={f('job_title')} placeholder="HR Director" className="pl-10 rounded-none" data-testid="register-job-title" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Industry</label>
                    <Input value={form.industry} onChange={f('industry')} placeholder="Luxury RTW" className="rounded-none" data-testid="register-industry" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">HQ Location</label>
                    <div className="relative"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input value={form.company_location} onChange={f('company_location')} placeholder="Milan, Italy" className="pl-10 rounded-none" data-testid="register-location" /></div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Company Website</label>
                  <div className="relative"><Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={form.company_website} onChange={f('company_website')} placeholder="https://yourbrand.com" className="pl-10 rounded-none" data-testid="register-website" /></div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Company LinkedIn <span className="text-muted-foreground normal-case">(optional)</span></label>
                  <div className="relative"><Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={form.company_linkedin} onChange={f('company_linkedin')} placeholder="linkedin.com/company/..." className="pl-10 rounded-none" data-testid="register-linkedin" /></div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">About the Company <span className="text-muted-foreground normal-case">(optional)</span></label>
                  <textarea value={form.company_description} onChange={f('company_description')} rows={2}
                    placeholder="A short description of your brand and hiring needs"
                    className="w-full border border-input bg-background px-3 py-2 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring resize-none" data-testid="register-description" />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Password</label>
                  <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={f('password')} required
                      placeholder="Min. 8 characters" className="pl-10 pr-10 rounded-none" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Confirm Password</label>
                  <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type={showConfirm ? 'text' : 'password'} value={form.confirm_password} onChange={f('confirm_password')} required
                      placeholder="Repeat password" className={`pl-10 pr-10 rounded-none ${
                        passwordMismatch ? 'border-red-400' : passwordMatch ? 'border-green-500' : ''
                      }`} />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordMismatch && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
                  {passwordMatch && <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>}
                </div>
                <p className="text-xs text-muted-foreground">Only company email addresses accepted. No Gmail, Yahoo or personal emails.</p>
                <Button type="submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80 mt-1">
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-5">
                Already have an account? <Link to="/login" className="text-foreground font-medium hover:underline">Sign in</Link>
              </p>
            </>
          )}

          {/* ── Verify email step ── */}
          {step === 'verify' && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold">Verify your email</h2>
                <p className="text-muted-foreground text-sm mt-1">We sent a code to <strong>{form.email}</strong></p>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
              {message && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm">{message}</div>}
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Verification Code</label>
                  <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} required
                    placeholder="000000" className="rounded-none text-center text-2xl tracking-[0.5em] font-mono" maxLength={6} />
                </div>
                <Button type="submit" disabled={loading || otp.length < 6} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80">
                  {loading ? 'Verifying...' : 'Verify Email'}
                </Button>
              </form>
              <div className="flex items-center justify-between mt-4">
                <button onClick={() => setStep('form')} className="text-sm text-muted-foreground hover:text-foreground">← Back</button>
                <button onClick={handleResend} disabled={resending}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50">
                  <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

export default Register;
